<template>
  <div ref="picker" class="picker program-select">
    <div :class="{ loading: isLoading, noicon: !isIcon }">
      <span v-if="isIcon" class="icon">&#x01F4CD;</span>
      <div class="input-wrapper">
        <input v-if="multiple" v-model="query" type="text" :placeholder="placeholder" @blur="blur" @input="search" @click="init" @focus="init" />
        <input v-else v-model="query" type="text" :placeholder="placeholder" :disabled="disabled" @blur="blur" @input="search" @click="init" @focus="init" @keyup.enter="update(selectedItems)" />
        <span v-if="!multiple && query" class="clear-icon" @click="clearSelection">&times;</span>
      </div>
      <a v-if="multiple && isSearching" class="closer" @click="isSearching = false">Close</a>
      <ul v-show="isSearching">
        <template v-if="filteredData.length">
          <li v-for="(item, index) in filteredData" :key="item.id || index" class="checkboxlabel" :class="{ on: isSelected(item), selected: isSelected(item) }" @mouseout="focus(-1)" @mouseover="focus(index)" @click="update(item)">
            <span v-if="multiple" class="checkbox" :class="{ checked: isSelected(item) }"></span>
            {{ item.label || item }}
          </li>
        </template>
        <li v-else class="no-results">No matching results</li>
      </ul>
    </div>
    <template v-if="multiple">
      <div v-if="selectedItems.length > 0" class="selections">
        <span>Selected:</span>
        <div v-for="(item, index) in selectedItems" :key="item.id || index" class="badge" @click="update(item)">{{ item.label || item }} <span class="badge-close">&times;</span></div>
      </div>
    </template>
  </div>
</template>
<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import debounce from "../../utils/debounce";

const props = defineProps({
  data: {
    type: [Array, Object],
    default: () => [],
  },
  selected: {
    type: [Object, Number, String, Array],
    default: null,
  },
  placeholder: {
    type: String,
    default: "Select",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  isIcon: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:selected", "updateId"]);

// Reactive state
const picker = ref(null);
const isLoading = ref(false);
const isSearching = ref(false);
const query = ref(props.selected?.label || props.selected || "");
const querySaved = ref("");
const focusIndex = ref(-1);
const filteredData = ref([]);

// Watch for changes in props.data and props.selected
watch(
  () => props.data,
  newData => {
    filteredData.value = newData;
  },
  { immediate: true }
);

watch(
  () => props.selected,
  newSelected => {
    if (newSelected) {
      query.value = newSelected.label || newSelected;
    } else {
      query.value = "";
    }
  },
  { immediate: true }
);

// Initialize selected items
const initSelect = () => {
  if (props.multiple) {
    return Array.isArray(props.selected) ? props.selected : [];
  }
  return props.selected;
};
const selectedItems = ref(initSelect());

const blur = () => {
  if (props.multiple) return;
  isSearching.value = false;
  if (focusIndex.value > -1) {
    update(filteredData.value[focusIndex.value]);
  } else if (selectedItems.value) {
    update(selectedItems.value);
  }
};

const focus = index => {
  focusIndex.value = index;
};

const init = () => {
  isSearching.value = true;
  if (!query.value) {
    filteredData.value = props.data;
  }
};

const search = debounce.debounceFunction(() => {
  isLoading.value = true;

  if (!query.value.trim()) {
    filteredData.value = props.data;
    isLoading.value = false;
    return;
  }

  filteredData.value = props.data.filter(item => {
    const itemText = String(item.label || item).toLowerCase();
    const searchText = query.value.toLowerCase().trim();
    return itemText.includes(searchText);
  });

  if (filteredData.value.length > 0 && !props.multiple) {
    selectedItems.value = filteredData.value[0];
  }

  isLoading.value = false;
}, 300);

const update = item => {
  if (!item) return;

  querySaved.value = query.value;

  if (props.multiple) {
    const currentSelected = Array.isArray(selectedItems.value) ? selectedItems.value : [];
    const itemIndex = currentSelected.findIndex(i => i === item);

    if (itemIndex === -1) {
      selectedItems.value = [...currentSelected, item];
    } else {
      selectedItems.value = currentSelected.filter((_, index) => index !== itemIndex);
    }

    isSearching.value = false;
    emit("update:selected", selectedItems.value);
    return;
  }

  isSearching.value = false;
  query.value = item.label || item;
  selectedItems.value = item;
  emit("update:selected", item);
  if (item.id) emit("updateId", item.id);
};

const isSelected = item => {
  if (props.multiple) {
    return selectedItems.value.includes(item);
  }
  return selectedItems.value === item;
};

const clearSelection = () => {
  query.value = "";
  selectedItems.value = null;
  filteredData.value = props.data;
  emit("update:selected", null);
  if (props.selected) {
    emit("updateId", null); // Also clear the ID if it was being used
  }
  isSearching.value = true;
};

const handleClickOutside = event => {
  if (picker.value && !picker.value.contains(event.target)) {
    isSearching.value = false;
    if (props.multiple) {
      querySaved.value = "";
      query.value = "";
    }
  }
};

onMounted(() => {
  filteredData.value = props.data;
  if (props.multiple) document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  if (props.multiple) document.removeEventListener("click", handleClickOutside);
});
</script>
<style lang="less">
.program-select {
  ul {
    width: 100%;
    max-height: 15rem;
    overflow-y: auto;
  }

  .no-results {
    padding: 0.5rem 1rem;
    color: @mid;
    font-style: italic;
  }

  .clear-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: @mid;
    font-size: 1.25rem;

    &:hover {
      color: @dark;
    }
  }
}
</style>
